import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import Modal from '~ComponentsImproved/Modal';
import { insideWrapperStyles } from '~ComponentsImproved/Modal';
import { Box, BlueButton, BlackButton, Button } from '~Styles';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useButton } from '~Hooks';
import { gql, useQuery } from '@apollo/client';
import Loader from '~ComponentsImproved/Loader';
import useI18n from '~Translations';
import useModal from '~ComponentsImproved/Modal/useModal';
import {
  AvailableTablesForBooking,
  AvailableTablesForBookingVariables,
} from './__queries__/AvailableTablesForBooking';
import { GetBookings_operativeViewer_bookingsForVenueAndDate } from '~Providers/BookingsProvider/api/__queries__';
import { useBookings } from '~Providers/BookingsProvider';
import TableSelectModal from './TableSelectModal';
import { GetTables_Venue_floorPlans_tables } from '~Scenes/Venue/__queries__';

const Wrapper = styled(Box)`
  box-shadow: ${insideWrapperStyles.boxShadow};
  border-radius: ${insideWrapperStyles.borderRadius};
  background-color: ${insideWrapperStyles.backgroundColor};
  min-width: 600px;
  max-width: 1024px;
  max-height: 80vh;
  width: 100%;
  overflow-y: auto;
  padding: 0;
`;

const InnerWrapper = styled.div`
  padding: 24px;
`;

const TablesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
  margin: 24px 0;
`;

const TableCard = styled.div<{ selected?: boolean }>`
  background-color: ${({ selected }) => (selected ? 'var(--colorBlueGray1300)' : 'transparent')};
  border: 2px solid
    ${({ selected }) => (selected ? 'var(--colorUIYellow)' : 'var(--colorBlueGray1000)')};
  border-radius: 8px;
  padding: 16px;
  transition: all 0.2s ease;
  text-align: center;
  min-height: 80px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const TableCode = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: var(--colorWhite);
  margin-bottom: 4px;
`;

const TableInfo = styled.div`
  font-size: 16px;
  color: var(--colorBlueGray200);
`;

const CapacityInfo = styled.div`
  font-size: 14px;
  color: var(--colorBlueGray300);
  margin-top: 4px;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
`;

const ButtonWrapper = styled(Box)`
  position: sticky;
  bottom: 0;
  background-color: var(--colorBlueGray1200);
  padding: 16px 24px;
`;

const ManualAssignButton = styled(Button)`
  background-color: transparent;
  border: 1px dashed var(--colorBlueGray700);
  color: var(--colorBlueGray200);
  font-size: 13px;
  min-height: unset;
  padding: 8px 12px;
  border-radius: 8px;
  text-transform: none;
`;

// GraphQL query for available tables
const AVAILABLE_TABLES_FOR_BOOKING = gql`
  query AvailableTablesForBooking($bookingId: ID!) {
    operativeViewer {
      availableTablesForBooking(bookingId: $bookingId) {
        resourceId
        tableIds
        capacity
        isCombination
      }
    }
  }
`;

interface Props {
  isShowing: boolean;
  toggle: () => void;
  booking: GetBookings_operativeViewer_bookingsForVenueAndDate;
  tables: GetTables_Venue_floorPlans_tables[];
  onComplete: () => void;
  onHighlightBooking?: (bookingId: string) => void;
}

const MoveTableModal: React.FC<Props> = ({
  isShowing,
  toggle,
  booking,
  tables,
  onComplete,
  onHighlightBooking,
}) => {
  const { i18n } = useI18n();
  const [selectedTableIds, setSelectedTableIds] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const bookings = useBookings();
  const tableSelectModal = useModal();

  const manualAssignButton = useButton({
    onClick: tableSelectModal.toggle,
  });

  // Query for available tables
  const availableTablesQuery = useQuery<
    AvailableTablesForBooking,
    AvailableTablesForBookingVariables
  >(AVAILABLE_TABLES_FOR_BOOKING, {
    variables: { bookingId: booking.id },
    skip: !isShowing, // Only query when modal is open
    fetchPolicy: 'network-only',
  });

  // Reset selection when modal closes
  useEffect(() => {
    if (!isShowing) {
      setSelectedTableIds([]);
    }
  }, [isShowing]);

  // Helper function to get table codes from table IDs
  const getTableCodes = (tableIds: string[]): string => {
    return tableIds
      .map((id) => tables.find((t) => t.id === id)?.code || i18n('Bookings.Unknown'))
      .join(' + ');
  };

  const cancelButton = useButton({
    onClick: () => {
      setSelectedTableIds([]);
      toggle();
    },
    disabled: isProcessing,
  });

  const confirmButton = useButton({
    onClick: async () => {
      setIsProcessing(true);
      const success = await bookings.actions.moveToAnotherTable(
        booking.id,
        selectedTableIds,
        booking.date,
      );

      if (success) {
        setSelectedTableIds([]);
        toggle();
        onComplete();

        // Trigger highlight animation for the moved booking
        if (onHighlightBooking) {
          onHighlightBooking(booking.id);
        }
      }

      setIsProcessing(false);
    },
    disabled: selectedTableIds.length === 0 || isProcessing,
  });

  return (
    <Modal isShowing={isShowing} hide={toggle}>
      <Wrapper padding="large">
        <InnerWrapper>
          <Paragraph level={2} color="var(--colorWhite)">
            {i18n('Bookings.SelectTableDescription', booking.partySize)}
          </Paragraph>

          {booking.tableIds.length > 0 && (
            <Box marginTop="small">
              <Paragraph level={3} color="var(--colorBlueGray200)">
                {i18n('Bookings.CurrentlyAssignedTo', getTableCodes(booking.tableIds))}
              </Paragraph>
            </Box>
          )}

          <Box marginTop="small">
            <ManualAssignButton {...manualAssignButton}>
              {i18n('Bookings.AssignToTableManually')}
            </ManualAssignButton>
          </Box>

          {availableTablesQuery.loading ? (
            <LoaderContainer>
              <Loader />
            </LoaderContainer>
          ) : availableTablesQuery.error ? (
            <Box marginTop="medium" padding="medium">
              <Paragraph level={2} color="var(--colorRed)">
                {i18n('Bookings.ErrorLoadingTables')}
              </Paragraph>
            </Box>
          ) : availableTablesQuery.data ? (
            <>
              {availableTablesQuery.data.operativeViewer.availableTablesForBooking.length === 0 ? (
                <Box marginTop="medium" padding="medium">
                  <Paragraph color="var(--colorBlueGray200)" level={2}>
                    {i18n('Bookings.NoAvailableTables')}
                  </Paragraph>
                </Box>
              ) : (
                <TablesGrid>
                  {availableTablesQuery.data.operativeViewer.availableTablesForBooking.map(
                    (option) => {
                      const tableDisplay = getTableCodes(option.tableIds);
                      const isSelected =
                        JSON.stringify([...selectedTableIds].sort()) ===
                        JSON.stringify([...option.tableIds].sort());

                      return (
                        <TableCard
                          key={option.resourceId}
                          selected={isSelected}
                          onClick={() => setSelectedTableIds(option.tableIds)}>
                          <TableCode>{tableDisplay}</TableCode>
                          {option.isCombination && (
                            <TableInfo>{i18n('Bookings.Combination')}</TableInfo>
                          )}
                          <CapacityInfo>{i18n('Bookings.Capacity', option.capacity)}</CapacityInfo>
                        </TableCard>
                      );
                    },
                  )}
                </TablesGrid>
              )}
            </>
          ) : null}
        </InnerWrapper>
        <ButtonWrapper display="flex" gap="small" marginTop="medium">
          <BlackButton fluid {...cancelButton}>
            {i18n('General.Cancel')}
          </BlackButton>
          <BlueButton fluid {...confirmButton}>
            {isProcessing ? i18n('Bookings.Moving') : i18n('Bookings.MoveToSelectedTable')}
          </BlueButton>
        </ButtonWrapper>
        <TableSelectModal
          isShowing={tableSelectModal.isShowing}
          toggle={tableSelectModal.toggle}
          selectedTableIds={selectedTableIds}
          isProcessing={isProcessing}
          confirmLabel={i18n('Bookings.MoveToSelectedTable')}
          confirmingLabel={i18n('Bookings.Moving')}
          onConfirm={async (tableIds) => {
            setIsProcessing(true);
            const success = await bookings.actions.moveToAnotherTable(
              booking.id,
              tableIds,
              booking.date,
            );

            if (success) {
              setSelectedTableIds([]);
              tableSelectModal.toggle();
              toggle();
              onComplete();

              if (onHighlightBooking) {
                onHighlightBooking(booking.id);
              }
            }

            setIsProcessing(false);
          }}
          title={i18n('Bookings.SelectTableDescription', booking.partySize)}
        />
      </Wrapper>
    </Modal>
  );
};

export default MoveTableModal;
